import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CaretIconSvg, CaretPointDirections } from "@pepdirect/v3/ui/icons";
import { LocalizationContext } from "v3/context/localization";
import { TenantContext } from "v3/context/tenant";
import { getLocalizedCheckoutCrumb } from "v3/helpers/localization";
import { CHECKOUT_STEPS } from "v3/types/checkout";
import {
  StyledBreadcrumbsWrapper,
  StyledBreadcrumbsScroller,
  StyledCrumbAndCaret,
  StyledCrumb,
} from "./index.styled";

interface BreadcrumbsProps {
  currentStep: CHECKOUT_STEPS;
  isLoading: boolean;
  onClick: (nextStep: CHECKOUT_STEPS) => void;
}

export const CheckoutBreadcrumbs: FC<BreadcrumbsProps> = ({
  currentStep,
  isLoading,
  onClick,
}) => {
  const { tenant } = useContext(TenantContext);
  const { localization } = useContext(LocalizationContext);
  const [currStep, setCurrStep] = useState<CHECKOUT_STEPS>(currentStep);
  const breadCrumbContainerRef = useRef<HTMLDivElement | null>(null);
  const breadCrumbRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { breadcrumbs } = localization?.checkout || {};
  const shippingMethodsEnabled =
    tenant?.checkout?.shippingOptions?.shippingMethodsEnabled || false;

  const orderedCrumbs = useMemo(
    () => [
      CHECKOUT_STEPS.shipping,
      ...(shippingMethodsEnabled ? [CHECKOUT_STEPS.shippingMethod] : []),
      CHECKOUT_STEPS.payment,
      CHECKOUT_STEPS.review,
    ],
    [shippingMethodsEnabled]
  );

  const scrollToSelectedStep = useCallback(
    (currentStep: CHECKOUT_STEPS) => {
      const selectedIndex = orderedCrumbs.findIndex(
        (step) => step === currentStep
      );
      if (
        selectedIndex >= 0 &&
        breadCrumbRefs.current[selectedIndex] &&
        breadCrumbContainerRef.current
      ) {
        const selectedItem = breadCrumbRefs.current[selectedIndex];
        const container = breadCrumbContainerRef.current;

        // Calculate the amount to scroll to center the item
        const scrollAmount =
          (selectedItem?.offsetLeft || 0) -
          container.offsetWidth / 2 +
          (selectedItem?.offsetWidth || 0) / 2;

        container.scrollTo({
          left: scrollAmount,
          behavior: "smooth",
        });
      }
    },
    [orderedCrumbs]
  );

  useEffect(() => {
    setCurrStep(currentStep);
    scrollToSelectedStep(currentStep);
  }, [currentStep, scrollToSelectedStep]);

  const handleClick = (crumb: CHECKOUT_STEPS, isDisabled?: boolean) => () => {
    if (isDisabled) return;
    setCurrStep(crumb);
    onClick(crumb);
  };

  return (
    <StyledBreadcrumbsWrapper>
      <StyledBreadcrumbsScroller ref={breadCrumbContainerRef}>
        {orderedCrumbs.map((crumb, i) => {
          // current step and steps after the current step are disabled
          const isDisabled = i >= orderedCrumbs.indexOf(currStep);
          return (
            <StyledCrumbAndCaret
              ref={(el) => {
                breadCrumbRefs.current[i] = el;
              }}
              key={`crumb-${i}`}
            >
              {!!i && (
                <CaretIconSvg
                  pointDirection={CaretPointDirections.right}
                  width="16px"
                  height="10px"
                />
              )}
              <StyledCrumb
                type="text"
                isCurrent={crumb === currStep}
                disabled={isDisabled || isLoading}
                onClick={handleClick(crumb, isDisabled)}
              >
                {getLocalizedCheckoutCrumb({
                  step: crumb,
                  translations: breadcrumbs,
                  shippingMethodsEnabled,
                })}
              </StyledCrumb>
            </StyledCrumbAndCaret>
          );
        })}
      </StyledBreadcrumbsScroller>
    </StyledBreadcrumbsWrapper>
  );
};
